import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type DisplaySummary } from '../api';
import { AppShell } from '../components/AppShell';

interface DisplayWithVenue extends DisplaySummary {
  venue: { id: string; name: string };
}

export function DisplaysPage() {
  const [displays, setDisplays] = useState<DisplayWithVenue[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      setErr(null);
      const data = await api.listAllDisplays();
      setDisplays(data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }

  useEffect(() => {
    load();
    // Auto-refresh every 5s so the status grid stays current with the
    // server-side heartbeat freshness check.
    const id = setInterval(load, 5_000);
    return () => clearInterval(id);
  }, []);

  const online = displays?.filter((d) => d.status === 'ONLINE') ?? [];
  const offline = displays?.filter((d) => d.status === 'OFFLINE') ?? [];
  const pairing = displays?.filter((d) => d.status === 'PAIRING') ?? [];

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Displays</h1>
          <p className="mt-1 text-muted">
            Health and status of every paired screen across every venue.
            A display is considered OFFLINE if it hasn't heartbeated in
            the last 60 seconds.
          </p>
        </div>

        {err && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {err}
          </div>
        )}

        {displays && (
          <div className="grid grid-cols-3 gap-4">
            <StatusTile
              label="Online"
              count={online.length}
              color="bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
              dotClass="bg-emerald-500"
            />
            <StatusTile
              label="Offline"
              count={offline.length}
              color="bg-red-500/10 text-red-300 border-red-500/30"
              dotClass="bg-red-500"
            />
            <StatusTile
              label="Pairing"
              count={pairing.length}
              color="bg-amber-500/10 text-amber-300 border-amber-500/30"
              dotClass="bg-amber-400"
            />
          </div>
        )}

        {!displays && !err && <div className="text-muted">Loading…</div>}

        {displays && displays.length === 0 && (
          <div className="card text-center text-muted">
            No displays paired yet. Add one from a venue page.
          </div>
        )}

        {displays && displays.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border">
            <table className="w-full divide-y divide-border text-sm">
              <thead className="bg-surface text-xs uppercase tracking-wider text-muted">
                <tr>
                  <th className="px-4 py-3 text-left">Display</th>
                  <th className="px-4 py-3 text-left">Venue</th>
                  <th className="px-4 py-3 text-left">Channel</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Last heartbeat</th>
                  <th className="px-4 py-3 text-left">Resolution</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {displays.map((d) => (
                  <DisplayRow key={d.id} d={d} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function StatusTile({
  label,
  count,
  color,
  dotClass,
}: {
  label: string;
  count: number;
  color: string;
  dotClass: string;
}) {
  return (
    <div className={`card flex items-center gap-3 border ${color}`}>
      <span className={`h-3 w-3 rounded-full ${dotClass}`} />
      <div>
        <div className="text-xs uppercase tracking-wider">{label}</div>
        <div className="mt-1 text-3xl font-bold">{count}</div>
      </div>
    </div>
  );
}

function statusDot(s: DisplaySummary['status']): string {
  switch (s) {
    case 'ONLINE':
      return 'bg-emerald-500';
    case 'OFFLINE':
      return 'bg-red-500';
    case 'PAIRING':
      return 'bg-amber-400';
  }
}

function DisplayRow({ d }: { d: DisplayWithVenue }) {
  return (
    <tr>
      <td className="px-4 py-3">
        <Link
          to={`/venues/${d.venueId}`}
          className="font-medium text-text hover:text-brand-light"
        >
          {d.name}
        </Link>
      </td>
      <td className="px-4 py-3 text-muted">{d.venue.name}</td>
      <td className="px-4 py-3 text-muted">
        {d.channelType.replace(/_/g, ' ').toLowerCase()}
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${statusDot(d.status)}`} />
          <span className="text-text">{d.status.toLowerCase()}</span>
        </span>
      </td>
      <td className="px-4 py-3 text-muted">{formatHeartbeat(d.lastHeartbeat)}</td>
      <td className="px-4 py-3 font-mono text-xs text-muted">
        {d.resolutionW && d.resolutionH
          ? `${d.resolutionW}×${d.resolutionH}`
          : '—'}
      </td>
    </tr>
  );
}

function formatHeartbeat(ts: string | null): string {
  if (!ts) return '—';
  const diffSec = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}
